import type { RefObject } from 'preact';
import { useState, useEffect, useMemo } from 'preact/hooks';
import { Props as ListViewProps } from '../UNSAFE_ListView';
import { PLACEHOLDER_STYLE_CLASS } from '../PRIVATE_VirtualizedCollection';
import { Metadata } from '../UNSAFE_Collection';

export const usePromotedSection = <K extends string | number, D>(
  data: ListViewProps<K, D>['data'],
  promotedSection: ListViewProps<K, D>['promotedSection'],
  rootRef: RefObject<HTMLDivElement>
) => {
  // tracking sparkle height
  const [sparkleHeight, setSparkleHeight] = useState<number>(0);

  const suggestions = useMemo(
    () => data && promotedSection && findSuggestions(data.data, promotedSection.count),
    [data, promotedSection]
  );

  useEffect(() => {
    // update sparkleHeight only when we have suggestions
    // avoid unnecessary iterating through elements
    if (suggestions) {
      const placeholder = rootRef.current?.querySelector(
        '.' + PLACEHOLDER_STYLE_CLASS
      ) as HTMLElement;
      let height = placeholder?.offsetHeight || 0;
      const suggestionItems = rootRef.current?.querySelectorAll('[data-oj-suggestion]');
      suggestionItems?.forEach((item) => (height += (item as HTMLElement).offsetHeight));
      setSparkleHeight(height);
    }
  }, [rootRef, suggestions]);

  return { sparkleHeight, suggestions };
};

/**
 * A helper function that finds the data with suggestions, and
 * returns the corresponding keys
 * @param dataState
 */
const findSuggestions = <K, D>(data: { data: D; metadata: Metadata<K> }[], count: number) => {
  if (count === 0) {
    return null;
  }

  const suggestionsData = data.slice(0, count);
  return suggestionsData.reduce((suggestions, value, index) => {
    const key = value.metadata.key;

    if (index === count - 1) {
      suggestions.set(key, 'end');
    } else {
      suggestions.set(key, true);
    }

    return suggestions;
  }, new Map<K, 'end' | true>());
};
